import React, { useState, useEffect, useRef } from 'react';
import { Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { LOGO_URL } from '../utils/assets';
import { abrirComSenha } from '../data/cliente';

/**
 * Porta do link de acompanhamento.
 *
 * O link sozinho não abre nada: quem chega aqui tem metade da credencial e
 * precisa da outra. A tela é de celular — o cliente abre no WhatsApp, e o
 * teclado sobe assim que a página carrega.
 */

const minutosAte = (iso) => {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso) - new Date()) / 60000));
};

const TelaDeSenha = ({ token, laboratorio, bloqueadoAte, onEntrou }) => {
  const [senha, setSenha] = useState('');
  const [vendo, setVendo] = useState(false);
  const [erro, setErro] = useState(null);
  const [bloqueio, setBloqueio] = useState(bloqueadoAte || null);
  const [restam, setRestam] = useState(() => minutosAte(bloqueadoAte));
  const [enviando, setEnviando] = useState(false);
  const campo = useRef(null);

  // Contagem viva: um "tente em 15 minutos" congelado deixa a pessoa recarregando
  // a página para descobrir se já pode.
  useEffect(() => {
    if (!bloqueio) return;
    const tick = () => {
      const m = minutosAte(bloqueio);
      setRestam(m);
      if (m === 0) { setBloqueio(null); setErro(null); }
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
  }, [bloqueio]);

  useEffect(() => { if (!bloqueio) campo.current?.focus(); }, [bloqueio]);

  const entrar = async (e) => {
    e.preventDefault();
    if (enviando || bloqueio || senha.length < 1) return;

    setEnviando(true);
    setErro(null);
    try {
      const r = await abrirComSenha(token, senha);
      if (r.situacao === 'ok') { onEntrou(r.sessao); return; }

      setSenha('');
      if (r.situacao === 'bloqueado') {
        setBloqueio(r.liberado_em);
      } else if (r.situacao === 'invalido') {
        setErro('Este link não vale mais. Peça um novo ao laboratório.');
      } else {
        setErro('Senha incorreta.');
        campo.current?.focus();
      }
    } catch {
      // Rede caída e senha errada são problemas diferentes, e a pessoa faz
      // coisas diferentes com cada um: um se resolve tentando de novo.
      setErro('Não deu para verificar agora. Confira a conexão e tente de novo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-slate-50 flex flex-col justify-center px-5 py-10">
      <div className="w-full max-w-sm mx-auto">
        <div className="text-center mb-8">
          <img src={LOGO_URL()} alt="" className="h-12 w-auto mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-slate-900">{laboratorio || 'Acompanhamento'}</h1>
          <p className="text-sm text-slate-500 mt-1">Acompanhamento dos seus serviços</p>
        </div>

        <form onSubmit={entrar} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <label htmlFor="senha-do-link" className="block text-sm font-medium text-slate-700 mb-2">
            Senha de acesso
          </label>

          <div className="relative">
            <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              id="senha-do-link"
              ref={campo}
              type={vendo ? 'text' : 'password'}
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              disabled={!!bloqueio}
              autoComplete="off"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              placeholder="a senha que o laboratório enviou"
              /* min-h de 52px: dedo em celular, não cursor. */
              className="w-full min-h-[52px] pl-11 pr-12 rounded-xl border border-slate-300 bg-white
                         text-base text-slate-900 placeholder:text-slate-400
                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                         disabled:bg-slate-100 disabled:text-slate-400"
            />
            <button
              type="button"
              onClick={() => setVendo(v => !v)}
              aria-label={vendo ? 'Ocultar senha' : 'Mostrar senha'}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-3 text-slate-400 hover:text-slate-600"
            >
              {vendo ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {/* Bloqueio e senha errada dizem coisas diferentes: um pede esperar,
              o outro pede tentar. Misturar os dois num "erro" genérico faria a
              pessoa insistir justamente quando insistir não adianta. */}
          {bloqueio ? (
            <p className="mt-3 text-sm text-slate-700 bg-slate-100 rounded-lg px-3 py-2.5">
              Muitas tentativas. Tente de novo em {restam} {restam === 1 ? 'minuto' : 'minutos'}.
            </p>
          ) : erro ? (
            <p role="alert" className="mt-3 text-sm text-slate-700 bg-slate-100 rounded-lg px-3 py-2.5">
              {erro}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={!!bloqueio || enviando || senha.length < 1}
            className="mt-4 w-full min-h-[52px] rounded-xl bg-blue-600 text-white text-base font-medium
                       flex items-center justify-center gap-2
                       hover:bg-blue-700 active:bg-blue-800 disabled:bg-slate-300 transition-colors"
          >
            {enviando ? 'Entrando...' : <>Entrar <ArrowRight size={18} /></>}
          </button>
        </form>

        <p className="text-xs text-slate-400 text-center mt-5 px-4">
          Não tem a senha? Peça ao laboratório pelo mesmo contato que mandou o link.
        </p>
      </div>
    </div>
  );
};

export default TelaDeSenha;
